import { AgTechQueryParam, ExportTypes, FilterSchemaType, LabBookSampleTypes, ModulesName, OdQueryParam } from "../enums";
import { helperFunctions } from "../helper";
import { IFileStoreAllResponse, IFilterSortAttributesAllResponse, IStatusAllResponse } from "../models";
import { URLConfig } from "../network";
import NetworkService from "../network/networkService";
import { Observable } from "rxjs";

const moduleNameMap: Partial<Record<ModulesName, string>> = {
  [ModulesName.FieldBook]: 'fieldBook',
  [ModulesName.RepBook]: 'repBook',
  [ModulesName.User]: 'user',
  [ModulesName.Location]: 'location',
  [ModulesName.Product]: 'product',
  [ModulesName.TreatmentProduct]: 'treatmentProduct',
  [ModulesName.Crop]: 'crop',
  [ModulesName.Season]: 'season',
  [ModulesName.Trial]: 'trial',
  [ModulesName.Protocol]: 'protocol',
  [ModulesName.TrialLocation]: 'trialLocation',
  [ModulesName.Inventory]: 'inventory',
  [ModulesName.ApplicationBook]: 'applicationbook',
  [ModulesName.TreatmentGroup]: 'triallocation/treatments', // cross check when permission get added for this module 
  [ModulesName.RepLevel]: 'observationbook',
  [ModulesName.EntryLevel]: 'ratingbook',
  [ModulesName.SoilSampleBook]: 'soilsamplebook',
  [ModulesName.TissueSampleBook]: 'tissuesamplebook',
  [ModulesName.NematodeSampleBook]: 'nematodeSamplebook',
  [ModulesName.Add_Inventory_Product]: 'Inventory/products', // cross check when permission get added for this module
  [ModulesName.Trial_Add_Inventory_Product]: 'trial/inventory', // cross check when permission get added for this module
  [ModulesName.TrialExperiments]: 'OnFarmTrialLocation',
  [ModulesName.Stakes]:'printing/stakes',
  [ModulesName.BarCode]:"fieldbook/barcode",
  [ModulesName.Trial_Substitute_Inventory]:"trial/inventory/substitute",
  [ModulesName.DemoTrial]:"demoTrial",
  [ModulesName.Demo]:"fieldbook/demo",
  [ModulesName.DEMO_TRIAL_DETAIL]: 'demotrial/inventory', // cross check when permission get added for this module
  [ModulesName.HarvestData]:"OnFarmHarvestData",
  [ModulesName.AgronomicData]:"OnFarmAgronomicData",
  [ModulesName.FieldBookCatalog] : 'FieldBookCatalog',
  [ModulesName.DemoFieldBookCatalog] : 'FieldBookCatalog/demo',
  [ModulesName.RepBookcatalog] : 'repBookcatalog',
};

export const lineOfBusinessData = (
  queryParams?: Array<{
    key: AgTechQueryParam;
    value: string[];
  }>
): Observable<Array<{ value: string; label: string }>> => {
  let url = URLConfig.getLineOfBusinessDataTrialMasterURL();

  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }

  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (repInfoResponse: unknown) => {
        if (repInfoResponse) {
          const res = repInfoResponse as unknown as IStatusAllResponse;

          const formattedData = res.values.map((item: string) => ({
            value: item,
            label: item,
          }));

          subscriber.next(formattedData);
        }
        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log("Error while fetching Line of Business data", error);
      }
    );
  });
};

const getExportUrl = (exportType: ExportTypes, id?: string): string => {
  switch (exportType) {
    case ExportTypes.Brand:
      return `${URLConfig.getBrandsTrialMasterURL()}`;
    case ExportTypes.Product:
      return `${URLConfig.getProductsMasterDataURL()}`;
    case ExportTypes.MasterLocation:
      return `${URLConfig.getLocationMasterDataURL()}`;
    case ExportTypes.MasterTreatments:
      return `${URLConfig.getTreatmentsMasterDataURL()}`;
    case ExportTypes.MasterTreatmentProduct:
      return `${URLConfig.getTreatmentProductsMasterDataURL()}`;
    case ExportTypes.Inventory:
      return `${URLConfig.getInventoryTrialMasterURL()}`;
    case ExportTypes.Trials:
      return `${URLConfig.getTrialsUrl()}`;
    case ExportTypes.TrialLocation:
      return `${URLConfig.getTrialLocationURL()}`;
    case ExportTypes.Treatments:
      return `${URLConfig.getTreatmentInfoTrialMasterURL(id)}`;
    case ExportTypes.Field_book:
      return `${URLConfig.getFieldBookExportBaseUrl()}`;
    case ExportTypes.Application_book:
      return `${URLConfig.getApplicationURL()}`;

    case ExportTypes.Rep_book:
      return `${URLConfig.getRepInfoTrialMasterURL()}`;
    case ExportTypes.Rating_book:
      return `${URLConfig.getObservationExportURL("ratingbook")}`;
    case ExportTypes.Observation_book:
      return `${URLConfig.getObservationExportURL("observationbook")}`;
    case ExportTypes.Location:
      return `${URLConfig.getTrialLocationByIdURL(id ?? "")}`;
    case ExportTypes.Stakes:
      return `${URLConfig.getStakesURL()}`;
    case ExportTypes.Tissue_sample:
      return `${URLConfig.getLabBookURL(LabBookSampleTypes.TISSUE)}`;
    case ExportTypes.Soil_sample:
      return `${URLConfig.getLabBookURL(LabBookSampleTypes.SOIL)}`;
    case ExportTypes.Nematode_sample:
      return `${URLConfig.getLabBookURL(LabBookSampleTypes.NEMATOD)}`;
    case ExportTypes.DemoTrial:
      return `${URLConfig.getDemoTrialsTrialMasterURL()}`;
    case ExportTypes.OnFarmAgronomicData:
      return `${URLConfig.getAgronomicData()}`;
    case ExportTypes.OnFarmHarvestData:
      return `${URLConfig.getHarvestData()}`;
    case ExportTypes.RepBookCatalog:
      return `${URLConfig.getRepBookCatalogURL()}`;
    case ExportTypes.Field_Book_Catalog:
      return `${URLConfig.getFieldBookCatalogURL()}`;
    case ExportTypes.TaskPlanner:
      return `${URLConfig.getToDoDetailsURL()}`;
    case ExportTypes.TrialBook:
      return `${URLConfig.getTrialBookDetailsURL()}`;
    case ExportTypes.FieldRep:
      return `${URLConfig.getFieldRepDetailsURL()}`;
    default:
      return "";
  }
};

export const exportData = (
  exportType: ExportTypes,
  requestPayload: any,
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }> | null,
  id?: string
): Observable<any> => {
  let url: string;
  url = `${getExportUrl(exportType, id)}/export`;

  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }

  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload, { responseType: "blob" }).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while adding product in inventory", error);
      }
    );
  });
};

export const importFile = (
  exportType: ExportTypes,
  file: File,
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<any> => {
  let url = `${getExportUrl(exportType)}/import`;
  if (queryParams && queryParams?.length > 0) {
    for (let param of queryParams) {
      url = helperFunctions.addQueryParam(url, param.key, param.value);
    }
  }
  const formData = new FormData();
  formData.append("file", file);
  return new Observable((subscriber) => {
    NetworkService.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }).then(
      (productResponse: unknown) => {
        if (productResponse) {
          const productsResponse = productResponse as unknown as any;
          subscriber.next(productsResponse);
        }
        subscriber.complete();
      },
      (error: any) => {
        subscriber.error(error);
        console.log("Error while fetching products master data", error);
      }
    );
  });
};

export const downloadFile = (
  exportType: ExportTypes,
  requestPayload: any,
  id?: string,
  queryParams?: Array<{
    key: AgTechQueryParam;
    value: string[];
  }>
): Observable<any> => {
  let url: string;
  url = `${getExportUrl(exportType, id)}/template`;

  if (queryParams && queryParams?.length > 0) {
    for (let param of queryParams) {
      url = helperFunctions.addQueryParam(url, param.key, param.value);
    }
  }

  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload, { responseType: "blob" }).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while adding product in inventory", error);
      }
    );
  });
};

export const getFileStoreData = (
  bucketName: string,
  resourceType: string,
  resourceIds: string[] | number[]
): Observable<IFileStoreAllResponse> => {
  const url = `${URLConfig.getFileStoreURL()}`;
  const requestPayload = {
    bucketName: bucketName,
    resourceType: resourceType,
    resourceIds: resourceIds,
  };
  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (res: unknown) => {
        const response = res as IFileStoreAllResponse;
        subscriber.next(response);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching files data", error);
      }
    );
  });
};

const getBulkColumnUpdateUrl = (moduleType: ModulesName): string => {
  const specificModuleUrl = moduleNameMap[moduleType];

  const trialMasterModules = [
    ModulesName.Inventory,
    ModulesName.DemoTrial,
  ];
  const isDataCollection = !trialMasterModules.includes(moduleType);
  const isMasterDataCollection = moduleType === ModulesName.Product;
  return specificModuleUrl
    ? URLConfig.getBulkColumnUpdateUrl(
        specificModuleUrl,
        isDataCollection,
        isMasterDataCollection,
      )
    : "";
};

// This function is used to update (Copy) multiple columns in a module 
export const bulkColumnUpdate = (
  moduleType: ModulesName,
  requestPayload: any,
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }> | null,
): Observable<any> => {
  let url: string;
  url = `${getBulkColumnUpdateUrl(moduleType)}`;

  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }

  return new Observable((subscriber) => {
    NetworkService.patch(url, requestPayload).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while copying columns", moduleType, error);
      }
    );
  });
};

// getBulkProductsByIdUpdateURL
const getBulkProductsByIdUpdateURL = (moduleType: ModulesName): string => {
  const specificModuleUrl = moduleNameMap[moduleType];

  return specificModuleUrl ? URLConfig.getBulkProductsByIdUpdateURL(specificModuleUrl) : "";
};

export const bulkColumnUpdateById = (
  moduleType: ModulesName,
  requestPayload: any,
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }> | null,
): Observable<any> => {
  let url: string;
  url = `${getBulkProductsByIdUpdateURL(moduleType)}`;

  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }

  return new Observable((subscriber) => {
    NetworkService.patch(url, requestPayload).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while copying columns", moduleType, error);
      }
    );
  });
};


export const getFilterSchema = (
  moduleType: ModulesName,
  filterSchemaType: FilterSchemaType,
  queryParams?: Array<{
    key: AgTechQueryParam;
    value: string[];
  }>
): Observable<IFilterSortAttributesAllResponse> => {
  const specificModuleUrl = moduleNameMap[moduleType] ?? '';
  let url = URLConfig.getFilterSchemaURL(specificModuleUrl, filterSchemaType);
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }
  return new Observable((subscriber) => {
    NetworkService.get(url).then(
      (res: unknown) => {
        if (res) {
          const response = res as unknown as IFilterSortAttributesAllResponse;
          subscriber.next(response);
        }
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log("Error while fetching filter schema", error);
      }
    );
  });
};


export const deleteCollectionBookEntries = (
  moduleType: ModulesName,
  requestPayload?: any,
  queryParams?: Array<{
    key: AgTechQueryParam | OdQueryParam;
    value: string[];
  }>
): Observable<any> => {
  const specificModuleUrl = moduleNameMap[moduleType] ?? '';

  let url = URLConfig.getDeleteBookEntriesURL(specificModuleUrl);

  // Append query parameters to the URL
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }

  return new Observable((subscriber) => {
    NetworkService.patch(url, requestPayload).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log(`Error while deleting entries for module: ${moduleType}`, error);
      }
    );
  });
};

export const deleteTrialMasterEntries = (
  moduleType: ModulesName,
  requestPayload?: any,
  queryParams?: Array<{
    key:  OdQueryParam;
    value: string[];
  }>
): Observable<any> => {
  const specificModuleUrl = moduleNameMap[moduleType] ?? '';

  let url = URLConfig.getDeleteTrialMasterEntriesURL(specificModuleUrl);

  // Append query parameters to the URL
  for (let param of queryParams || []) {
    url = helperFunctions.addQueryParam(url, param.key, param.value);
  }

  return new Observable((subscriber) => {
    NetworkService.post(url, requestPayload).then(
      (res: unknown) => {
        subscriber.next(res);
        subscriber.complete();
      },
      (error: Error) => {
        subscriber.error(error);
        console.log(`Error while deleting entries for module: ${moduleType}`, error);
      }
    );
  });
};